import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { ReactComponent as CheckMarkIcon } from "../../../assets/check_mark.svg";
import { GradientBorderBox } from "./shared";
import { StyledImageButton } from "./styled";
import BuildingDuplicateWarning from "../../../components/BuildingDuplicateWarning";
import SaveButtonImage from "../../../assets/save-to-DB-button.png";
import SaveButtonBeforeImage from "../../../assets/save-to-DB-button-before.png";
import {
  ExtractedContract,
  BuildingCheckResponse,
} from "../../../types/contract";
import { FlowState } from "../types";
import { useFileOperations } from "../useFileOperations";
import { useFieldControl } from "../useFieldControl";
import { useContractResolution } from "../useContractResolution";
import { useBuildingDuplicateCheck } from "../useBuildingDuplicateCheck";
import { ContractFieldsGrid } from "./ContractFieldsGrid";

interface DetailedCheckerViewProps {
  aggregatedContracts: ExtractedContract[];
  finalContracts: ExtractedContract[];
  onSelectContract: (
    contract: ExtractedContract | null,
    shouldScroll: boolean,
  ) => void;
  selectedContract: ExtractedContract | null;
  confirmedContracts: Set<number>;
  needsReviewContracts: Set<number>;
  onDetailChange: (field: keyof ExtractedContract, value: string) => void;
  handleConfirmAndNext: () => void;
  handleMarkForReview: () => void;
  handleSaveFinalData: () => void;
  setFlowState: (state: FlowState) => void;
  handlePrev: () => void;
  handleNext: () => void;
  flowState: FlowState;
  detailScrollRef: React.RefObject<HTMLDivElement | null>;
  buildingChecks: Map<number, BuildingCheckResponse>;
  conflictResolutions: Map<
    number,
    { building: "keep" | "update" | null; owner: "keep" | "update" | null }
  >;
  handleBuildingResolutionChange: (
    contractIdx: number,
    resolution: "keep" | "update",
  ) => void;
  handleOwnerResolutionChange: (
    contractIdx: number,
    resolution: "keep" | "update",
  ) => void;
}

export const DetailedCheckerView: React.FC<DetailedCheckerViewProps> = ({
  aggregatedContracts,
  finalContracts,
  selectedContract,
  onSelectContract,
  confirmedContracts,
  needsReviewContracts,
  onDetailChange,
  handleConfirmAndNext,
  handleMarkForReview,
  handleSaveFinalData,
  handlePrev,
  flowState,
  detailScrollRef,
  buildingChecks,
  conflictResolutions,
  handleBuildingResolutionChange,
  handleOwnerResolutionChange,
}) => {
  const [sourceMenuAnchorEl, setSourceMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const saveButtonRef = useRef<HTMLDivElement>(null); // Declare ref for save button area

  // Custom hooks
  const { handleOpenFile, sourceFiles } = useFileOperations(selectedContract);
  const { currentContractIdx, currentResolution } = useContractResolution(
    selectedContract,
    finalContracts,
    conflictResolutions,
  );
  const { isFieldDisabled } = useFieldControl(currentResolution || null);
  const duplicateCheckResult = useBuildingDuplicateCheck(
    selectedContract,
    finalContracts,
    buildingChecks,
    conflictResolutions,
  );

  useEffect(() => {
    if (
      aggregatedContracts.length > 0 &&
      aggregatedContracts.length === confirmedContracts.size
    ) {
      if (saveButtonRef.current) {
        saveButtonRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }
  }, [confirmedContracts.size, aggregatedContracts.length]);

  const handleOpenSourceMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSourceMenuAnchorEl(event.currentTarget);
  };

  const handleCloseSourceMenu = () => {
    setSourceMenuAnchorEl(null);
  };

  return (
    <Grid container spacing={2}>
      {/* Left Pane */}
      <Grid item xs={12} md={4}>
        <GradientBorderBox sx={{ maxHeight: "70vh", overflow: "auto" }}>
          <List dense sx={{ padding: 0 }}>
            {aggregatedContracts.map((contract) => {
              const isConfirmed = confirmedContracts.has(contract.id!);
              const isNeedsReview = needsReviewContracts.has(contract.id!);
              return (
                <ListItemButton
                  key={contract.id}
                  selected={selectedContract?.id === contract.id}
                  onClick={() => onSelectContract(contract, false)}
                  sx={{
                    backgroundColor: "inherit",
                    "&:hover": {
                      backgroundColor: isConfirmed ? "#d8ffefff" : "#ffe3e3ff",
                    },
                    "&.Mui-selected": {
                      backgroundColor: isConfirmed ? "#ccffea" : "#ffd1d1",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: isConfirmed ? "#c4ffe6ff" : "#ffc7c7ff",
                    },
                  }}
                >
                  <ListItemText
                    primary={`${contract.property_name} ${contract.room_name}`}
                    secondary={contract.lessee_name}
                  />
                  {isNeedsReview && (
                    <ListItemIcon>
                      <HelpOutline color="warning" />
                    </ListItemIcon>
                  )}
                  {isConfirmed && (
                    <ListItemIcon>
                      <CheckMarkIcon
                        style={{ width: "24px", height: "24px" }}
                      />
                    </ListItemIcon>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </GradientBorderBox>
      </Grid>

      {/* Right Pane */}
      <Grid item xs={12} md={8}>
        <Box>
          <GradientBorderBox innerRef={detailScrollRef}>
            {" "}
            {/* Removed sx to fix double scrollbar */}
            {selectedContract ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  {" "}
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ flexGrow: 1, mb: 0 }}
                  >
                    読み込み結果
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenSourceMenu}
                    disabled={sourceFiles.length === 0}
                    sx={{
                      ml: 1,
                      display: "inline-block",
                      color: "#FF7292", // Blue text
                      border: `2px solid #FF7292`, // Blue border
                      borderRadius: "50px", // Rounded
                      bgcolor: "white",
                      px: 2, // Horizontal padding
                      py: 0.2, // Vertical padding
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      boxShadow: `
                        0 0 0 5px white, 
                        4px 6px 10px #FF7292
                      `, // Glowy shadow with blue
                      "&:hover": {
                        border: `2px solid #FF7292`,
                        bgcolor: "rgba(194, 140, 255, 0.05)",
                        boxShadow: `
                          0 0 0 5px white,
                          6px 9px 10px #FF7292 
                        `,
                      },
                    }}
                  >
                    参照ファイル一覧
                  </Button>
                  <Menu
                    anchorEl={sourceMenuAnchorEl}
                    open={Boolean(sourceMenuAnchorEl)}
                    onClose={handleCloseSourceMenu}
                  >
                    {sourceFiles.length > 0 ? (
                      sourceFiles.map((file) => (
                        <MenuItem
                          key={file}
                          onClick={() => {
                            handleOpenFile(file);
                            handleCloseSourceMenu();
                          }}
                        >
                          {file}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>参照ファイルがありません</MenuItem>
                    )}
                  </Menu>
                </Box>
                <ContractFieldsGrid
                  selectedContract={selectedContract}
                  isFieldDisabled={isFieldDisabled}
                  onDetailChange={onDetailChange}
                />

                {/* Building Duplicate Warning */}
                {duplicateCheckResult && (
                  <BuildingDuplicateWarning
                    existingBuilding={
                      duplicateCheckResult.checkResult!.building!
                    }
                    existingOwner={duplicateCheckResult.checkResult!.owner}
                    buildingMatchType={
                      duplicateCheckResult.dynamicBuildingMatch || undefined
                    }
                    ownerMatchType={duplicateCheckResult.dynamicOwnerMatch.type}
                    matchReason={duplicateCheckResult.dynamicOwnerMatch.reason}
                    buildingResolution={
                      duplicateCheckResult.currentResolutions.building
                    }
                    ownerResolution={
                      duplicateCheckResult.currentResolutions.owner
                    }
                    onBuildingResolutionChange={(resolution) =>
                      handleBuildingResolutionChange(
                        duplicateCheckResult.contractIdx,
                        resolution,
                      )
                    }
                    onOwnerResolutionChange={(resolution) =>
                      handleOwnerResolutionChange(
                        duplicateCheckResult.contractIdx,
                        resolution,
                      )
                    }
                    currentContract={selectedContract!}
                    dataSource={duplicateCheckResult.checkResult!.data_source}
                  />
                )}

                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #e0e0e0",
                    display: "flex",
                    justifyContent: "center", // 中央ぞろえに変更
                    alignItems: "center",
                    gap: 3, // ボタン間の隙間を広げる
                  }}
                >
                  <Button
                    variant="contained" // containedに変更
                    onClick={handlePrev}
                    disabled={
                      !selectedContract ||
                      aggregatedContracts.findIndex(
                        (c) => c.id === selectedContract.id,
                      ) === 0
                    }
                    sx={{
                      flex: 4,
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      fontSize: "16px",
                      pl: 4,
                      pr: 4,
                      backgroundColor: "rgb(64, 144, 255)", // 青色
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                      boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgb(64, 144, 255)",
                        border: "4px solid rgba(240, 232, 249, 0.9)",
                      },
                    }}
                  >
                    ◀戻る
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmAndNext}
                    sx={{
                      flex: 10,
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      fontSize: "16px",
                      pl: 4,
                      pr: 4,
                      backgroundColor: "rgba(255, 95, 149, 1)", // 赤色
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                      boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 95, 149, 1)",
                        border: "4px solid rgba(240, 232, 249, 0.9)",
                      },
                    }}
                  >
                    次へ(確認済み)
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleMarkForReview}
                    sx={{
                      flex: 4,
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      fontSize: "16px",
                      pl: 4,
                      pr: 4,
                      backgroundColor: "rgba(255, 189, 23, 1)", // 黄色
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                      boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgb(255, 189, 23, 1)",
                        border: "4px solid rgba(240, 232, 249, 0.9)",
                      },
                    }}
                  >
                    後で確認▶
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography>左のリストから項目を選択してください。</Typography>
            )}
          </GradientBorderBox>
          <Box sx={{ mt: 2, display: "flex" }}></Box>
        </Box>
      </Grid>
      {aggregatedContracts.length > 0 && (
        <Box ref={saveButtonRef} sx={{ width: 300, margin: "0 auto" }}>
          <StyledImageButton
            src={
              aggregatedContracts.length === confirmedContracts.size
                ? SaveButtonImage
                : SaveButtonBeforeImage
            }
            alt={
              aggregatedContracts.length === confirmedContracts.size
                ? "DBへ保存"
                : "全ての確認が完了すると押せるようになります"
            }
            style={{ width: "100%", height: "auto" }}
            onClick={
              aggregatedContracts.length !== confirmedContracts.size ||
              flowState === "saving"
                ? undefined
                : handleSaveFinalData
            } // onClickをStyledImageButtonに移動
            is_disabled={
              aggregatedContracts.length !== confirmedContracts.size ||
              flowState === "saving"
            } // is_disabled props を追加
          />
        </Box>
      )}
    </Grid>
  );
};
